import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Chip,
  Stack,
  Avatar,
  IconButton,
  Divider,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PercentIcon from '@mui/icons-material/Percent';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import { useTeams, useUsers, useAuth, useNotification } from '../../../hooks';
import { hasuraClient } from '../../../utils/graphqlClient';
import { GET_USER_BONUS_COEFFICIENTS_BY_PERIOD_QUERY } from '../../../graphql/queries';
import { BULK_UPSERT_USER_BONUS_COEFFICIENTS_MUTATION } from '../../../graphql/mutations';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => CURRENT_YEAR - 3 + i);

// İsimden 2 harf initial üret (avatar için).
const getInitials = (firstName, lastName) => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  if (l) return l.slice(0, 2).toUpperCase();
  return '??';
};

const UserBonusCoefficientsBulkModal = ({ open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();

  const isRootUser = currentUser?.role === 'ROOT';

  const { teams: allTeams = [], isLoading: teamsLoading } = useTeams();
  const { users: allUsers = [], isLoading: usersLoading } = useUsers();

  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [yil, setYil] = useState(CURRENT_YEAR);
  const [ceyrek, setCeyrek] = useState(1);

  const [coefficients, setCoefficients] = useState({});
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedTeamId('');
      setYil(CURRENT_YEAR);
      setCeyrek(1);
      setCoefficients({});
    }
  }, [open]);

  const selectedTeam = useMemo(
    () => allTeams.find((tm) => tm.id === parseInt(selectedTeamId, 10)),
    [allTeams, selectedTeamId]
  );

  const teamCompanyId = selectedTeam?.company_id || currentUser?.companyId;
  const teamManagerId = selectedTeam?.team_manager_id || null;

  const teamMembers = useMemo(() => {
    if (!selectedTeamId) return [];
    const teamIdNum = parseInt(selectedTeamId, 10);
    return allUsers
      .filter((u) => u.teamId === teamIdNum)
      .sort((a, b) =>
        (`${a.firstName || ''} ${a.lastName || ''}`).localeCompare(
          `${b.firstName || ''} ${b.lastName || ''}`
        )
      );
  }, [allUsers, selectedTeamId]);

  const fetchExisting = useCallback(async () => {
    if (!selectedTeamId || !yil || !ceyrek || teamMembers.length === 0) {
      setCoefficients({});
      return;
    }
    setIsLoadingExisting(true);
    try {
      const memberIds = teamMembers.map((m) => m.id);
      const where = {
        user_id: { _in: memberIds },
        yil: { _eq: parseInt(yil, 10) },
        ceyrek: { _eq: parseInt(ceyrek, 10) },
      };
      if (!isRootUser && teamCompanyId) {
        where.company_id = { _eq: teamCompanyId };
      }
      const data = await hasuraClient.query(
        GET_USER_BONUS_COEFFICIENTS_BY_PERIOD_QUERY,
        { where }
      );
      const existing = data?.user_bonus_coefficients || [];
      const existingMap = {};
      existing.forEach((row) => {
        existingMap[row.user_id] = String(row.katsayi);
      });
      const next = {};
      teamMembers.forEach((m) => {
        next[m.id] = existingMap[m.id] !== undefined ? existingMap[m.id] : '1';
      });
      setCoefficients(next);
    } catch (err) {
      console.error('Existing coefficients fetch error:', err);
      const next = {};
      teamMembers.forEach((m) => {
        next[m.id] = '1';
      });
      setCoefficients(next);
    } finally {
      setIsLoadingExisting(false);
    }
  }, [selectedTeamId, yil, ceyrek, teamMembers, isRootUser, teamCompanyId]);

  useEffect(() => {
    fetchExisting();
  }, [fetchExisting]);

  const handleCoefficientChange = (userId, value) => {
    setCoefficients((prev) => ({ ...prev, [userId]: value }));
  };

  const allFiltersSelected = !!selectedTeamId && !!yil && !!ceyrek;
  const periodLabel = `${yil} / Q${ceyrek}`;

  const handleSave = async () => {
    if (!allFiltersSelected || teamMembers.length === 0) return;
    setIsSaving(true);
    try {
      const yilInt = parseInt(yil, 10);
      const ceyrekInt = parseInt(ceyrek, 10);
      const objects = teamMembers.map((m) => {
        const raw = coefficients[m.id];
        const parsed = parseFloat(raw);
        return {
          user_id: m.id,
          company_id: m.companyId || teamCompanyId,
          yil: yilInt,
          ceyrek: ceyrekInt,
          katsayi: Number.isFinite(parsed) ? parsed : 1,
        };
      });
      const result = await hasuraClient.mutate(
        BULK_UPSERT_USER_BONUS_COEFFICIENTS_MUTATION,
        { objects }
      );
      const affected =
        result?.insert_user_bonus_coefficients?.affected_rows ?? objects.length;
      showNotification(
        t('userBonusCoefficients.bulk.saved', { count: affected }),
        'success'
      );
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Bulk upsert error:', err);
      const msg = err?.message || t('userBonusCoefficients.errors.createError');
      showNotification(msg, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EditNoteIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {t('userBonusCoefficients.bulk.title')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {t('userBonusCoefficients.bulk.subtitle',
                  'Ekip + dönem seç, katsayıları aynı anda gir')}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{ color: 'common.white' }}
            disabled={isSaving}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Filter şeridi */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={{ minWidth: 280 }}>
                <InputLabel>
                  {t('userBonusCoefficients.bulk.team')}
                </InputLabel>
                <Select
                  value={selectedTeamId}
                  label={t('userBonusCoefficients.bulk.team')}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  disabled={teamsLoading}
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsIcon fontSize="small" color="action" />
                    </InputAdornment>
                  }
                >
                  {allTeams.map((tm) => (
                    <MenuItem key={tm.id} value={tm.id}>
                      {tm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                <InputLabel>
                  {t('userBonusCoefficients.bulk.yil')}
                </InputLabel>
                <Select
                  value={yil}
                  label={t('userBonusCoefficients.bulk.yil')}
                  onChange={(e) => setYil(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <CalendarTodayIcon fontSize="small" color="action" />
                    </InputAdornment>
                  }
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                <InputLabel>
                  {t('userBonusCoefficients.bulk.ceyrek')}
                </InputLabel>
                <Select
                  value={ceyrek}
                  label={t('userBonusCoefficients.bulk.ceyrek')}
                  onChange={(e) => setCeyrek(e.target.value)}
                >
                  {[1, 2, 3, 4].map((q) => (
                    <MenuItem key={q} value={q}>
                      Q{q}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {allFiltersSelected && teamMembers.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1.5, color: 'text.secondary' }}
            >
              <Chip
                onClick={() => {}}
                size="small"
                label={selectedTeam?.name || '—'}
                icon={<GroupsIcon sx={{ fontSize: 14 }} />}
                variant="outlined"
                sx={{ height: 24, fontWeight: 500 }}
              />
              <Chip
                onClick={() => {}}
                size="small"
                label={periodLabel}
                icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                variant="outlined"
                sx={{ height: 24, fontWeight: 500, fontFamily: 'monospace' }}
              />
              <Typography variant="caption" sx={{ ml: 'auto' }}>
                {t('userBonusCoefficients.bulk.memberCount',
                  '{{count}} kişi listelendi', { count: teamMembers.length })}
              </Typography>
            </Stack>
          )}
        </Box>

        {/* İçerik */}
        <Box sx={{ p: 2.5 }}>
          {!allFiltersSelected ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.disabled' }}>
              <EditNoteIcon sx={{ fontSize: 52, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2">
                {t('userBonusCoefficients.bulk.noTeamSelected')}
              </Typography>
            </Box>
          ) : isLoadingExisting || usersLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : teamMembers.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.disabled' }}>
              <GroupsIcon sx={{ fontSize: 52, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2">
                {t('userBonusCoefficients.bulk.noMembers')}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 1.5 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary' }}>
                      {t('userBonusCoefficients.bulk.kisi')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', width: 200 }}
                    >
                      {t('userBonusCoefficients.bulk.katsayi')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((m, idx) => {
                    const fullName =
                      `${m.firstName || ''} ${m.lastName || ''}`.trim() || `#${m.id}`;
                    const isLeader = teamManagerId && m.id === teamManagerId;
                    return (
                      <TableRow
                        key={m.id}
                        sx={{
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                          borderTop: idx === 0 ? 0 : undefined,
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor: isLeader
                                  ? theme.palette.warning.light
                                  : theme.palette.primary.light,
                                color: 'common.white',
                              }}
                            >
                              {getInitials(m.firstName, m.lastName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {fullName}
                              </Typography>
                              {isLeader && (
                                <Chip
                                  onClick={() => {}}
                                  icon={<StarIcon sx={{ fontSize: 12 }} />}
                                  label={t('userBonusCoefficients.bulk.lider')}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 10, mt: 0.25 }}
                                />
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={coefficients[m.id] ?? '1'}
                            onChange={(e) =>
                              handleCoefficientChange(m.id, e.target.value)
                            }
                            inputProps={{
                              step: '0.01',
                              min: '0',
                              style: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <PercentIcon sx={{ fontSize: 14, color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ maxWidth: 160 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Divider orientation="vertical" flexItem sx={{ mr: 'auto', display: 'none' }} />
        {allFiltersSelected && teamMembers.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {t('userBonusCoefficients.bulk.saveHint',
              '{{count}} kullanıcı için katsayı kaydedilecek',
              { count: teamMembers.length })}
          </Typography>
        )}
        <Button onClick={onClose} disabled={isSaving} color="inherit">
          {t('common.cancel', 'İptal')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !allFiltersSelected ||
            teamMembers.length === 0 ||
            isLoadingExisting ||
            isSaving
          }
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', minWidth: 120 }}
        >
          {isSaving
            ? t('modals.form.saving', 'Kaydediliyor...')
            : t('userBonusCoefficients.bulk.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserBonusCoefficientsBulkModal;
